import React, { useEffect, useState } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { fetchDharmaReport } from '../../lib/api';

const Tile = ({ label, value, sub, accent, testId }) => (
  <div
    data-testid={testId}
    className={`p-7 border ${
      accent
        ? 'bg-[#7C9CB1] text-[#F4F8F9] border-[#7C9CB1]'
        : 'bg-[#F4F8F9] border-[#C1D7E2] text-[#7C9CB1]'
    }`}
  >
    <p
      className={`font-mono text-[10px] tracking-[0.3em] uppercase mb-4 ${
        accent ? 'text-[#F4F8F9]/80' : 'text-[#90B0BF]'
      }`}
    >
      {label}
    </p>
    <p className="font-serif text-3xl md:text-4xl font-light leading-none">{value}</p>
    {sub && (
      <p
        className={`mt-3 font-mono text-[11px] tracking-[0.2em] ${
          accent ? 'text-[#F4F8F9]/80' : 'text-[#90B0BF]'
        }`}
      >
        {sub}
      </p>
    )}
  </div>
);

const DharmaWatchdog = () => {
  const [report, setReport] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetchDharmaReport().then(setReport).catch(() => setErr('Could not load dharma report.'));
  }, []);

  if (err) return <p className="text-[#7C9CB1]">{err}</p>;
  if (!report)
    return <div data-testid="dharma-loading" className="h-40 bg-[#C1D7E2]/40 animate-pulse" />;

  const r = report.totals;
  const healthy = r.coverage_ratio >= 1.0;
  const StatusIcon = healthy ? ShieldCheck : AlertTriangle;

  return (
    <div data-testid="dharma-view" className="space-y-10">
      <header className="border-b border-[#C1D7E2] pb-6 flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-2">
            Dharma Watchdog · Read-only
          </p>
          <h3 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light leading-tight">
            Verified Proof-of-Seva
          </h3>
          <p className="mt-2 font-serif italic text-[#90B0BF] max-w-2xl">
            {report.policy}
          </p>
        </div>
        <div
          data-testid="dharma-coverage-badge"
          className={`inline-flex items-center gap-3 px-5 py-3 border ${
            healthy
              ? 'border-[#7C9CB1] bg-[#C1D7E2]/30 text-[#7C9CB1]'
              : 'border-[#90B0BF] bg-[#AAC6D4]/30 text-[#7C9CB1]'
          }`}
        >
          <StatusIcon size={20} strokeWidth={1.3} />
          <div>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase">
              Coverage ratio
            </p>
            <p className="font-serif text-2xl leading-none">
              {(r.coverage_ratio * 100).toFixed(2)}%
            </p>
          </div>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Tile
          accent
          testId="dharma-total-minted"
          label="Total minted (events)"
          value={`${r.total_minted_from_events.toFixed(2)}`}
          sub="Traced to orders, refills, dispenses"
        />
        <Tile
          testId="dharma-carried-buyer"
          label="Buyer wallets"
          value={r.carried_buyer.toFixed(2)}
          sub="1 USD : 1 SEVA at checkout"
        />
        <Tile
          testId="dharma-carried-creator"
          label="Creator wallets"
          value={r.carried_creator.toFixed(2)}
          sub="10 % of attributed orders"
        />
        <Tile
          testId="dharma-carried-partner"
          label="Partner wallets"
          value={r.carried_partner.toFixed(2)}
          sub="Refill + pay-per-use rewards"
        />
      </section>

      <section data-testid="dharma-by-source" className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-3">
            Minted by source
          </p>
          <ul className="divide-y divide-[#C1D7E2]">
            {Object.entries(report.minted_by_source).map(([k, v]) => (
              <li key={k} className="flex items-baseline justify-between py-3">
                <span className="font-serif text-lg text-[#7C9CB1] capitalize">
                  {k.replace(/_/g, ' ')}
                </span>
                <span className="font-mono text-[#7C9CB1]">{v.toFixed(2)} SEVA</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-3">
            Physical event counts
          </p>
          <ul className="divide-y divide-[#C1D7E2]">
            {Object.entries(report.event_counts).map(([k, v]) => (
              <li key={k} className="flex items-baseline justify-between py-3">
                <span className="font-serif text-lg text-[#7C9CB1] capitalize">
                  {k.replace(/_/g, ' ')}
                </span>
                <span className="font-mono text-[#7C9CB1]">{v}</span>
              </li>
            ))}
          </ul>
          {!healthy && (
            <p className="mt-6 font-serif italic text-[#7C9CB1] bg-[#AAC6D4]/25 border border-[#90B0BF] p-4">
              Coverage below 1.0 indicates carried balances exceed events on record.
              Check partner seed grants or freshly-awarded events pending persistence.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default DharmaWatchdog;
